/**
 * Setting up the map, as the order it actually has to happen in.
 *
 * ⚠ **Multi-step, order-dependent setup was being presented as one flat
 * surface**, with the rules written as prose instead of enforced by the flow. The
 * map was the worst of it: spread across the setup page, a sidebar
 * *during play*, and a pin action nothing walked anyone through.
 *
 * The steps have **real dependencies**, which is why a waterfall fits:
 *
 *  1. the image **and the scale** — without the scale a journey has no length
 *     and travel quietly costs nothing;
 *  2. the **cells** export — required, it is the ground itself;
 *  3. **rivers and routes** — optional, but they are what a party fords and
 *     what stops it getting lost;
 *  4. **the world** — and it runs once;
 *  5. **pinning the places** — or every generated place is off the map and the
 *     first march is silently uncostable.
 *
 * ⚠ **The constraint that must be impossible to miss** was a sentence buried in
 * a sidebar essay: the image and the GeoJSON have to be exported at the same
 * extent. A cropped or redrawn map puts the picture and the terrain out of
 * register, and *nothing can detect that but you*. It is the first thing on the
 * page now.
 *
 * ⚠ **This is also where the world import moved to.** It was in the
 * sidebar of the live play surface — three file inputs and an essay, beside the
 * transcript, during a session.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, ApiError } from '../api.js';
import { MAP_IMAGE_TYPES, uploadMapImage } from './uploadMap.js';
import { WorldImport } from './WorldImport.js';

/** What the campaign already has, so each step can say whether it is done. */
interface MapState {
  readonly hasImage: boolean;
  readonly scaleMiles: number | null;
  readonly hasTerrain: boolean;
  readonly places: number;
  readonly unpinned: number;
}

/**
 * One step, with its state on the label.
 *
 * ⚠ **Says what breaks if it is skipped**, rather than only whether it is done.
 * The whole finding was that a player "cannot tell what to do first, which
 * parts are optional, what breaks if they skip one" — so a green tick alone
 * would fix a third of it.
 */
function Step({
  n,
  title,
  done,
  optional,
  breaks,
  children,
}: {
  readonly n: number;
  readonly title: string;
  readonly done: boolean;
  readonly optional?: boolean;
  readonly breaks: string;
  readonly children?: React.ReactNode;
}) {
  return (
    <section className="space-y-2 rounded-md border border-neutral-200 p-4 dark:border-neutral-800">
      <h2 className="flex flex-wrap items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        <span>
          {n} · {title}
        </span>
        {optional && <span className="normal-case font-normal">(optional)</span>}
        <span
          className={
            done
              ? 'normal-case font-normal text-emerald-700 dark:text-emerald-400'
              : 'normal-case font-normal text-neutral-500 dark:text-neutral-400'
          }
        >
          {done ? 'done' : 'not yet'}
        </span>
      </h2>
      {!done && <p className="text-xs text-neutral-600 dark:text-neutral-400">{breaks}</p>}
      {children}
    </section>
  );
}

export function MapSetupPage() {
  const { campaignId } = useParams();
  const [state, setState] = useState<MapState | null>(null);
  const [scale, setScale] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!campaignId) return;
    // Read-only, and each piece answers for one step. A single "is the map set
    // up" flag could not say *which* step is missing, which is the whole point.
    // ⚠ The campaign row and `/places`, not an invented endpoint. The map
    // surface reaches the table over the socket, which a standalone page does
    // not have — but the row already carries the image key, and the scale lives
    // in `worldState.worldMapScaleMiles` where `PUT /map-scale` writes it.
    const [campaign, places] = await Promise.all([
      api.get<{
        campaign: { worldMapKey?: string | null; worldState?: { worldMapScaleMiles?: number } };
      }>(`/api/campaigns/${campaignId}`),
      api.get<{ places: { waypoint?: unknown }[] }>(`/api/campaigns/${campaignId}/places`),
    ]);
    const terrain = await fetch(`/api/campaigns/${campaignId}/terrain`)
      .then((r) => r.ok)
      .catch(() => false);
    setState({
      hasImage: Boolean(campaign.campaign.worldMapKey),
      scaleMiles: campaign.campaign.worldState?.worldMapScaleMiles ?? null,
      hasTerrain: terrain,
      places: places.places.length,
      unpinned: places.places.filter((p) => !p.waypoint).length,
    });
  }, [campaignId]);

  useEffect(() => {
    void refresh().catch(() => setNote('could not read what the map already has'));
  }, [refresh]);

  if (!campaignId) return null;

  const uploadImage = (file: File) => {
    setBusy(true);
    setNote(null);
    // ⚠ **Two calls, not one** — the same fix `NewCampaignPage`'s setup wizard
    // needed. The upload only ever wrote an `assets` row; `campaigns.
    // world_map_key` (what actually makes `hasImage` true below) was
    // previously set only by `host:set_world_map`, a live table's own socket
    // panel this standalone page has no connection to. Without the second
    // call, uploading here looked like it worked and `refresh()` would keep
    // reporting `hasImage: false` forever.
    void uploadMapImage(campaignId, 'world_map', file)
      .then((key) => api.put(`/api/campaigns/${campaignId}/world-map`, { key }))
      .then(() => refresh())
      .catch((e: unknown) => setNote(e instanceof ApiError ? e.message : 'that image was refused'))
      .finally(() => setBusy(false));
  };

  const saveScale = () => {
    const miles = Number(scale);
    if (!Number.isFinite(miles) || miles <= 0) {
      setNote('the scale is how many miles the map is across — a number greater than zero');
      return;
    }
    setBusy(true);
    void api
      .put(`/api/campaigns/${campaignId}/map-scale`, { scaleMiles: miles })
      .then(() => refresh())
      .catch((e: unknown) => setNote(e instanceof ApiError ? e.message : 'that scale was refused'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Setting up the map</h1>
        <Link to={`/campaigns/${campaignId}`} className="text-sm underline underline-offset-2">
          back to the table
        </Link>
      </div>

      {/* ⚠ **First, because nothing can detect it afterwards.** A cropped or
          redrawn image puts the picture and the terrain out of register, and
          the party walks over ground that is not where it is drawn. It was one
          sentence inside a sidebar essay. */}
      <p className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
        <strong>Export the image and the map data at the same extent.</strong> If you crop or
        redraw one of them, the picture and the ground stop lining up — the party will walk over
        terrain that is not where it looks. Nothing here can detect that; only you can.
      </p>

      {note && (
        <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
          {note}
        </p>
      )}

      <Step
        n={1}
        title="The image, and how wide it is"
        done={Boolean(state?.hasImage) && state?.scaleMiles !== null && state?.scaleMiles !== undefined}
        breaks="Without the scale a journey has no length, so travel costs no time at all and nothing says why."
      >
        <input
          type="file"
          accept={MAP_IMAGE_TYPES}
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadImage(file);
          }}
          className="block text-sm file:mr-3 file:rounded-md file:border file:border-neutral-300 file:bg-white file:px-3 file:py-1 file:text-sm file:font-medium file:text-neutral-900 hover:file:bg-neutral-50 dark:file:border-neutral-700 dark:file:bg-neutral-900 dark:file:text-neutral-100 dark:hover:file:bg-neutral-800"
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-neutral-500 dark:text-neutral-400" htmlFor="map-scale">
            Miles across
          </label>
          <input
            id="map-scale"
            value={scale}
            onChange={(e) => setScale(e.target.value)}
            placeholder={state?.scaleMiles !== null && state?.scaleMiles !== undefined ? String(state.scaleMiles) : '600'}
            className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          <button
            type="button"
            disabled={busy || scale.trim() === ''}
            onClick={saveScale}
            className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
          >
            Set the scale
          </button>
        </div>
      </Step>

      <Step
        n={2}
        title="The ground"
        done={Boolean(state?.hasTerrain)}
        breaks="The cells export is the ground itself. Without it there is no terrain under the party, so a march cannot be costed."
      >
        {/* ⚠ **Moved here from the play surface** (#4a): this was three file
            inputs and an essay in the sidebar, beside the transcript, during a
            session. Rivers and routes are step 3 and ride the same import. */}
        <WorldImport campaignId={campaignId} onImported={() => void refresh()} />
      </Step>

      <Step
        n={3}
        title="Rivers and roads"
        optional
        done={Boolean(state?.hasTerrain)}
        breaks="They ride the same import as the ground. Without them nothing is forded and a road cannot spare the party a lost check."
      />

      <Step
        n={4}
        title="The world"
        done={(state?.places ?? 0) > 0}
        breaks="Somewhere to be. Generate it from the campaign's premise, or write the places yourself."
      >
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {/* Stated because it cannot be undone: the model is not
              deterministic, so a second run would write a different world. */}
          Generating runs <strong>once</strong> — from the table page, where the premise is.
        </p>
      </Step>

      <Step
        n={5}
        title="Where the places are"
        done={state !== null && state.places > 0 && state.unpinned === 0}
        breaks="A place with no pin is off the map, and a march to it cannot be costed — silently."
      >
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {state && state.places > 0
            ? `${state.places - state.unpinned} of ${state.places} pinned. Pin the rest on the table's map.`
            : 'Nothing to pin yet — write or generate some places first.'}
        </p>
      </Step>
    </div>
  );
}
